/**
 * HTTP Error Logger Utility
 * 
 * Centraliza a lógica de logging de erros HTTP para o Oráculo.
 * Extrai 22 campos enriquecidos do erro Axios e envia para o sistema de observabilidade.
 * 
 * Uso:
 * ```javascript
 * import { logHttpError } from '@/core/utils/http-error-logger';
 * 
 * try {
 *   const response = await axios.post(url, data, config);
 *   return response.data;
 * } catch (error) {
 *   logHttpError(error, { method: 'POST', url: url, data: data });
 *   throw error;
 * }
 * ```
 */

import oraculoLogs from '@/core/services/oraculo-logs.service';

/**
 * Registra erro HTTP no Oráculo com 22 campos enriquecidos
 * 
 * @param {Error} error - Erro capturado (geralmente AxiosError)
 * @param {Object} requestInfo - Informações da requisição
 * @param {string} requestInfo.method - Método HTTP (GET, POST, PUT, DELETE, etc.)
 * @param {string} requestInfo.url - URL completa da requisição
 * @param {Object} [requestInfo.data] - Dados enviados na requisição
 * @param {Object} [requestInfo.params] - Query parameters da requisição
 * @param {boolean} [requestInfo.sanitizeData=false] - Se true, substitui dados sensíveis por '***'
 */
export const logHttpError = (error, requestInfo) => {
  // 🛡️ GUARD: Previne ciclo infinito - NÃO logar erros do próprio Oráculo
  if (requestInfo.url && requestInfo.url.includes('/api/logs/ingest')) {
    console.warn('⚠️ [http-error-logger] Erro do Oráculo detectado - IGNORANDO para evitar loop infinito');
    return;
  }
  
  // Sanitiza dados sensíveis se necessário (ex: passwords)
  let requestData = requestInfo.data || {};
  if (requestInfo.sanitizeData && typeof requestData === 'object') {
    requestData = Object.keys(requestData).reduce((acc, key) => {
      acc[key] = '***';
      return acc;
    }, {});
  }

  // Extrai todos os 22 campos enriquecidos do erro
  const enrichedDetails = {
    // Error Core
    errorType: error.name || 'AxiosError',
    errorCode: error.code || 'UNKNOWN',
    errorMessage: error.message || 'No error message',
    errorStack: error.stack || 'No stack trace',

    // Request Information (from requestInfo parameter)
    requestUrl: requestInfo.url || 'unknown',
    requestMethod: requestInfo.method || 'unknown',
    requestData: JSON.stringify(requestData) || '{}',
    requestParams: JSON.stringify(requestInfo.params || {}) || '{}',
    requestTimeout: error.config?.timeout || 0,

    // Authentication Information (secure preview)
    hasAuthToken: !!(error.config?.headers?.Authorization),
    authTokenPreview: error.config?.headers?.Authorization 
      ? error.config.headers.Authorization.substring(0, 20) + '...' 
      : 'No token',
    contentType: error.config?.headers?.['Content-Type'] || 'not set',
    withCredentials: error.config?.withCredentials || false,

    // Response Information
    responseStatus: error.response?.status || 0, // 0 = Network Error (sem resposta)
    responseStatusText: error.response?.statusText || 'Network Error',
    responseData: JSON.stringify(error.response?.data || {}) || '{}',
    responseHeaders: JSON.stringify(error.response?.headers || {}) || '{}',

    // Metadata
    isAxiosError: error.isAxiosError || true,
    detectedBy: 'try-catch-service',
    timestamp: new Date().toISOString(),
    baseURL: error.config?.baseURL || 'not set',
  };

  // Envia para o Oráculo
  oraculoLogs.logHttpError({
    method: requestInfo.method,
    path: requestInfo.url,
    // ⚠️ IMPORTANTE: Oráculo exige status >= 100
    // Se não houver resposta (Network Error), usar 503 Service Unavailable
    status: error.response?.status || 503,
    details: enrichedDetails
  });
};

/**
 * Helper para criar requestInfo object facilmente
 * 
 * @param {string} method - Método HTTP
 * @param {string} url - URL da requisição
 * @param {Object} [options] - Opções adicionais
 * @returns {Object} requestInfo formatado
 */
export const createRequestInfo = (method, url, options = {}) => {
  return {
    method,
    url,
    data: options.data,
    params: options.params,
    sanitizeData: options.sanitizeData || false
  };
};
